(() => {
  const SOURCE_WRAPPER_SELECTOR = '.kleep-desktop-wrapper, .kleep-mobile-wrapper';
  const SOURCE_BUTTON_SELECTOR = '.kleep-button';
  const SOURCE_LABEL_SELECTOR = '.kleep-btn-text';
  const LATE_BOOT_RETRY_INTERVAL = 500;
  const LATE_BOOT_RETRY_TIMEOUT = 10000;

  const isRendered = (element) => element.getClientRects().length > 0;

  const isDisplayed = (element) => {
    const styles = getComputedStyle(element);
    return styles.display !== 'none' && styles.visibility !== 'hidden';
  };

  const collectDisplayedText = (root) => {
    let text = '';

    root.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        text += node.textContent;
      } else if (node.nodeType === Node.ELEMENT_NODE && isDisplayed(node)) {
        text += collectDisplayedText(node);
      }
    });

    return text;
  };

  const collapseWhitespace = (text) => text.replace(/\s+/g, ' ').trim();

  if (!customElements.get('kleep-size-mirror')) {
    customElements.define(
      'kleep-size-mirror',
      class KleepSizeMirror extends HTMLElement {
        connectedCallback() {
          this.button = this.querySelector('[data-kleep-mirror-button]');
          this.label = this.querySelector('[data-kleep-mirror-label]');
          if (!this.button || !this.label) return;

          this.wrappers = [];
          this.sync = this.sync.bind(this);
          this.requestSync = this.requestSync.bind(this);
          this.handleClick = this.handleClick.bind(this);

          this.mutationObserver = new MutationObserver(this.requestSync);
          this.resizeObserver = new ResizeObserver(this.requestSync);

          this.button.addEventListener('click', this.handleClick);
          window.addEventListener('resize', this.requestSync, { passive: true });
          window.addEventListener('load', this.requestSync);

          this.startLateBootRetry();
          this.sync();
        }

        disconnectedCallback() {
          if (this.button) this.button.removeEventListener('click', this.handleClick);
          window.removeEventListener('resize', this.requestSync);
          window.removeEventListener('load', this.requestSync);
          if (this.mutationObserver) this.mutationObserver.disconnect();
          if (this.resizeObserver) this.resizeObserver.disconnect();
          this.stopLateBootRetry();
          if (this.syncFrame) cancelAnimationFrame(this.syncFrame);
        }

        startLateBootRetry() {
          const startedAt = performance.now();

          this.lateBootTimer = setInterval(() => {
            this.sync();

            const found = this.hidden === false;
            const expired = performance.now() - startedAt >= LATE_BOOT_RETRY_TIMEOUT;
            if (found || expired) this.stopLateBootRetry();
          }, LATE_BOOT_RETRY_INTERVAL);
        }

        stopLateBootRetry() {
          if (!this.lateBootTimer) return;

          clearInterval(this.lateBootTimer);
          this.lateBootTimer = null;
        }

        bindWrappers() {
          const found = Array.from(document.querySelectorAll(SOURCE_WRAPPER_SELECTOR));
          const unchanged =
            found.length === this.wrappers.length &&
            found.every((wrapper, index) => wrapper === this.wrappers[index]);

          if (unchanged) return;

          this.wrappers = found;
          this.mutationObserver.disconnect();
          this.resizeObserver.disconnect();

          this.wrappers.forEach((wrapper) => {
            this.mutationObserver.observe(wrapper, {
              attributes: true,
              characterData: true,
              childList: true,
              subtree: true,
            });
            this.resizeObserver.observe(wrapper);
          });
        }

        resolveSourceButton() {
          this.bindWrappers();

          for (const wrapper of this.wrappers) {
            const button = wrapper.querySelector(SOURCE_BUTTON_SELECTOR);
            if (button && isRendered(button)) return button;
          }

          return null;
        }

        readSourceLabel(sourceButton) {
          const labelRoot = sourceButton.querySelector(SOURCE_LABEL_SELECTOR) || sourceButton;
          return collapseWhitespace(collectDisplayedText(labelRoot));
        }

        requestSync() {
          if (this.syncFrame) return;

          this.syncFrame = requestAnimationFrame(() => {
            this.syncFrame = null;
            this.sync();
          });
        }

        sync() {
          if (!this.button || !this.label) return;

          const sourceButton = this.resolveSourceButton();
          this.hidden = sourceButton === null;
          if (!sourceButton) return;

          const label = this.readSourceLabel(sourceButton);
          if (label && label !== this.label.textContent) {
            this.label.textContent = label;
          }
        }

        handleClick(event) {
          event.preventDefault();

          const sourceButton = this.resolveSourceButton();
          if (!sourceButton) {
            this.hidden = true;
            return;
          }

          sourceButton.click();
        }
      }
    );
  }
})();
